import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import configData from "../config.json";
import { getToken } from "../utils/auth";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";


const UserManagement = () => {
  const navigate = useNavigate();
  const notify = (message, style) => {
      if (style === "success") {
      toast.success(message, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          icon: false, // remove the icon
      });
      } else {
      toast.error(message, {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          icon: false, // remove the icon
      });
      }
  };

  // --- STATE ---
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [refreshData, setRefreshData] = useState(false);

  const [addingUser, setAddingUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [currentEmail, setCurrentEmail] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    email: null,
    role: null,
    current_balance: "",
    daily_credits: "",
    monthly_initial_credits: "",
    key_id: ""
    });

  // --- FETCH USERS ---
  const fetchMIAUsersData = async () => {
    try {
      const token = getToken();
      const res = await fetch(`${configData.DEMO_URL}/Users_data`, {
        method: "GET",
        headers: { "Content-Type": "application/json", "x-access-token": token },
      });

      if (!res.ok) {
        navigate("/");
        return;
      }

      const response = await res.json();
      return response.MIA_Users;
    } catch (error) {
      navigate("/");
    }
  };

  const transformUserData = (tupleData) => {
    if (!Array.isArray(tupleData)) return [];
    return tupleData.map(
      ([id, email, role, current_balance, daily_credits, monthly_initial_credits, key_id]) => ({
        id,
        email,
        role,
        current_balance,
        daily_credits,
        monthly_initial_credits,
        key_id,
      })
    );
  };

  useEffect(() => {
    const fetchData = async () => {
      const MIA_Data = await fetchMIAUsersData();
      if (!MIA_Data) return;
      const { miaUsersData, all_roles } = MIA_Data;
      setUsers(transformUserData(miaUsersData));
      setRoles(all_roles);
    };

    fetchData();
  }, [refreshData]);

  // --- ADD USER ---
  const addMIAUsersData = async (payload) => {
    try {
        const token = getToken();
        const res = await fetch(`${configData.DEMO_URL}/Users_data`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "x-access-token": token,
        },
        body: JSON.stringify(payload),
        });

        if (!res.ok) {
        const err = await res.json();
        notify(`Failed to add user: ${err.message || res.status}`, 'error');
        return false;
        }

        notify(`${payload.userEmail} added successfully`, 'success');
        setRefreshData((prev) => !prev);
        return true;
    } catch (error) {
        notify("Failed to add user", 'error');
        return false;
    }
    };

  const addUserHandleSave = async () => {
    const payload = {
        userEmail: formData.email,
        userRole: formData.role,
        current_balance: Number(formData.current_balance || 0),
        daily_credits: Number(formData.daily_credits || 0),
        monthly_initial_credits: Number(formData.monthly_initial_credits || 0),
        key_id: formData.key_id || null,
    };

    const success = await addMIAUsersData(payload);
    if (!success) return;

    setIsModalOpen(false);
    setFormData({
        email: "Username",
        role: "MIA_USER",
        current_balance: "",
        daily_credits: "",
        monthly_initial_credits: "",
        key_id: "",
    });
    };

  // --- EDIT USER ---
  const editMIAUsersData = async (payload) => {
    try {
        const token = getToken();
        const res = await fetch(`${configData.DEMO_URL}/Users_data`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            "x-access-token": token,
        },
        body: JSON.stringify(payload),
        });

        if (!res.ok) {
        const err = await res.json();
        notify(`Failed to update user: ${err.message || res.status}`, 'error');
        return false;
        }

        notify("User updated successfully", 'success');
        setRefreshData((prev) => !prev);
        return true;
    } catch (error) {
        notify("Failed to update user", 'error');
        return false;
    }
    };

    const editUserHandleSave = async () => {
        const payload = {
            userEmail: currentEmail,              // original email (identifier)
            editUserEmail: formData.email,
            editUserRole: formData.role,
            current_balance: Number(formData.current_balance),
            daily_credits: Number(formData.daily_credits),
            monthly_initial_credits: Number(formData.monthly_initial_credits),
            key_id: formData.key_id || null,
        };

        const success = await editMIAUsersData(payload);
        if (!success) return;

        setIsModalOpen(false);
        setEditingUser(null);
        setCurrentEmail(null);
        setFormData({
            email: "",
            role: "Viewer",
            current_balance: "",
            daily_credits: "",
            monthly_initial_credits: "",
            key_id: "",
        });
    };

  // --- DELETE USER ---
  const deleteMIAUsersData = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.email}?`)) return;

    try {
      const token = getToken();
      const res = await fetch(`${configData.DEMO_URL}/Users_data`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", "x-access-token": token },
        body: JSON.stringify({ userEmail: user.email }),
      });

      const data = await res.json();
      if (res.ok) {
        notify(`${user.email} deleted successfully`, 'success');
        setUsers(users.filter((u) => u.id !== user.id));
      } else {
        notify(`Failed to delete user: ${data.message || res.status}`, 'error');
      }
    } catch (error) {
      notify(`Failed to delete user: ${error.message}`, 'error');
    }
  };


  // --- MODAL HANDLING ---
  const openModal = (user = null) => {
    if (user) {
        setEditingUser(user);
        setFormData({
        email: user.email || "Username",
        role: user.role || null,
        current_balance: user.current_balance || 0,
        daily_credits: user.daily_credits || 0,
        monthly_initial_credits: user.monthly_initial_credits || 0,
        key_id: user.key_id || ""
        });
    } else {
        setEditingUser(null);
        setFormData({
        email: "Username",
        role: null,
        current_balance: "",
        daily_credits: "",
        monthly_initial_credits: "",
        key_id: ""
        });
    }
    setIsModalOpen(true);
    };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingUser) {
      await editUserHandleSave();
    } else {
      await addUserHandleSave();
    }
  };

  return (
    <div style={styles.container}>
          <ToastContainer
            position="top-center"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            />
      <style>{hoverStyles}</style>

      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Team Members</h1>
          <p style={styles.subtitle}>Manage your team members and their account permissions here.</p>
        </div>
        <button style={styles.addButton} onClick={() => openModal()}>+ Add User</button>
      </div>

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.theadRow}>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Role</th>
              <th style={styles.th}>Current Balance</th>
              <th style={styles.th}>Daily Credits</th>
              <th style={styles.th}>Monthly Initial Credits</th>
              <th style={styles.th}>Key ID</th>
              <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} style={styles.tr} className="user-row">
                <td style={styles.td}>
                  <div style={styles.userInfo}>
                    <div style={styles.avatar}>{user.email.charAt(0).toUpperCase()}</div>
                    <div style={{ marginLeft: '8px' }}>
                      <div style={styles.userName}>{user.email}</div>
                    </div>
                  </div>
                </td>
                <td style={styles.td}>{user.role}</td>
                <td style={styles.td}>{user.current_balance}</td>
                <td style={styles.td}>{user.daily_credits}</td>
                <td style={styles.td}>{user.monthly_initial_credits}</td>
                <td style={styles.td}>{user.key_id}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                  <button style={styles.actionBtn} onClick={() => {setCurrentEmail(user.email); openModal(user)}}>Edit</button>
                  <button style={{ ...styles.actionBtn, color: '#ef4444' }} onClick={() => deleteMIAUsersData(user)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div style={styles.overlay}>
            <div style={styles.modal}>
            <h2 style={{ marginBottom: "1rem" }}>
                {editingUser ? "Edit User" : "Add User"}
            </h2>

            <form onSubmit={handleSubmit}>

                {/* Email */}
                <div style={styles.formGroup}>
                <label style={styles.label}>Email</label>
                <input
                    style={styles.input}
                    required
                    value={formData.email}
                    onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                    }
                    readOnly={!!editingUser}
                />
                </div>

                {/* Role */}
                <div style={styles.formGroup}>
                <label style={styles.label}>Role</label>
                <select
                    style={styles.input}
                    value={formData.role}
                    onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                    }
                >
                    {roles.length
                    ? roles.map((r) => (
                        <option key={r} value={r}>
                            {r}
                        </option>
                        ))
                    : (
                        <>
                        <option>Admin</option>
                        <option>Editor</option>
                        <option>Viewer</option>
                        </>
                    )}
                </select>
                </div>

                {/* Current Balance */}
                <div style={styles.formGroup}>
                <label style={styles.label}>Current Balance</label>
                <input
                    style={styles.input}
                    type="number"
                    step="0.01"
                    value={formData.current_balance}
                    onChange={(e) =>
                    setFormData({ ...formData, current_balance: e.target.value })
                    }
                />
                </div>

                {/* Daily Credits */}
                <div style={styles.formGroup}>
                <label style={styles.label}>Daily Credits</label>
                <input
                    style={styles.input}
                    type="number"
                    step="0.01"
                    value={formData.daily_credits}
                    onChange={(e) =>
                    setFormData({ ...formData, daily_credits: e.target.value })
                    }
                />
                </div>

                {/* Monthly Initial Credits */}
                <div style={styles.formGroup}>
                <label style={styles.label}>Monthly Initial Credits</label>
                <input
                    style={styles.input}
                    type="number"
                    step="0.01"
                    value={formData.monthly_initial_credits}
                    onChange={(e) =>
                    setFormData({
                        ...formData,
                        monthly_initial_credits: e.target.value
                    })
                    }
                />
                </div>

                {/* upload ID */}
                <div style={styles.formGroup}>
                <label style={styles.label}>Key ID</label>
                <input
                    style={styles.input}
                    type="text"
                    value={formData.key_id}
                    onChange={(e) =>
                    setFormData({ ...formData, key_id: e.target.value })
                    }
                />
                </div>

                {/* Actions */}
                <div style={styles.modalActions}>
                <button
                    type="button"
                    style={styles.cancelBtn}
                    onClick={() => setIsModalOpen(false)}
                >
                    Cancel
                </button>
                <button type="submit" style={styles.submitBtn}>
                    Save
                </button>
                </div>

            </form>
            </div>
        </div>
        )}

    </div>
  );
};

// --- STYLES ---
const hoverStyles = `.user-row:hover { background-color: #f9fafb; }`;
const styles = {
  container: {
    padding: '2.5rem 1.25rem',
    backgroundColor: '#f3f4f6',
    minHeight: '100dvh',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },

  header: {
    maxWidth: '62.5rem',          // ~1000px
    margin: '0 auto 2rem auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap',
  },

  title: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#111827',
    margin: 0,
  },

  subtitle: {
    color: '#6b7280',
    marginTop: '0.25rem',
    fontSize: '0.95rem',
  },

  addButton: {
    backgroundColor: '#4f46e5',
    color: 'white',
    padding: '0.65rem 1.25rem',
    borderRadius: '0.5rem',
    border: 'none',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s',
    whiteSpace: 'nowrap',
  },

  tableContainer: {
    maxWidth: '62.5rem',
    margin: '0 auto',
    backgroundColor: 'white',
    borderRadius: '0.75rem',
    boxShadow: '0 0.25rem 0.375rem -0.0625rem rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
  },

  theadRow: {
    backgroundColor: '#f9fafb',
    borderBottom: '0.0625rem solid #e5e7eb',
  },

  th: {
    padding: '1rem',
    color: '#374151',
    fontSize: '0.875rem',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },

  tr: {
    borderBottom: '0.0625rem solid #f3f4f6',
    transition: 'background 0.2s',
  },

  td: {
    padding: '1rem',
    verticalAlign: 'middle',
  },

  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },

  avatar: {
    width: '2.25rem',
    height: '2.25rem',
    borderRadius: '50%',
    backgroundColor: '#e0e7ff',
    color: '#4338ca',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '0.875rem',
    flexShrink: 0,
  },

  userName: {
    fontWeight: '600',
    color: '#111827',
    fontSize: '0.95rem',
  },

  roleBadge: {
    padding: '0.25rem 0.625rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },

  actionBtn: {
    background: 'none',
    border: 'none',
    color: '#4f46e5',
    fontWeight: '500',
    marginLeft: '1rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },

  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },

  modal: {
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '1rem',
    width: '100%',
    maxWidth: '25rem',
  },

  formGroup: {
    marginBottom: '1rem',
  },

  label: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginBottom: '0.375rem',
  },

  input: {
    width: '100%',
    padding: '0.625rem',
    borderRadius: '0.5rem',
    border: '0.0625rem solid #d1d5db',
    boxSizing: 'border-box',
    fontSize: '0.875rem',
  },

  modalActions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1.5rem',
  },

  cancelBtn: {
    flex: 1,
    padding: '0.625rem',
    borderRadius: '0.5rem',
    border: '0.0625rem solid #d1d5db',
    background: 'white',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },

  submitBtn: {
    flex: 1,
    padding: '0.625rem',
    borderRadius: '0.5rem',
    border: 'none',
    background: '#4f46e5',
    color: 'white',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '0.875rem',
  },
};


export default UserManagement;
